import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IoJournalOutline, IoChevronForward, IoPeopleOutline, IoDocumentTextOutline } from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import { useAuth } from "@/context/AuthContext";
import { useAsyncData } from "@/lib/useAsyncData";
import { SkeletonCards, RetryNotice } from "@/components/DataStates";
import { listCarnetAccessGranted } from "@/lib/api";

export default function MesAccompagnes() {
  const nav = useNavigate();
  const { loading, isAuthenticated, isAdmin } = useAuth();
  const { data: list, loading: lLoading, error, reload } = useAsyncData("accompagnes_granted", listCarnetAccessGranted);

  useEffect(() => {
    if (!loading && (!isAuthenticated || !isAdmin)) nav("/hub", { replace: true });
  }, [loading, isAuthenticated, isAdmin, nav]);

  if (!isAdmin) return null;
  const members = list || [];

  return (
    <DojoShell crumbs={[{ label: "Mes accompagnés" }]}>
      <div style={s.head}>
        <div style={s.badge}><IoPeopleOutline size={22} /></div>
        <div>
          <h1 style={s.h1}>Mes accompagnés</h1>
          <p style={s.sub}>Les conteurs qui t'ont confié l'accès complet à leur carnet. Ouvre le leur pour suivre leur progression et les accompagner directement.</p>
        </div>
      </div>

      {(lLoading && !list) ? (
        <SkeletonCards count={4} height={120} min={280} />
      ) : error ? (
        <RetryNotice onRetry={reload} label="Impossible de charger tes accompagnés." />
      ) : members.length === 0 ? (
        <div style={s.empty} data-testid="accompagnes-empty">
          Personne ne t'a encore donné accès à son carnet.<br />
          Depuis la page <strong>Membres</strong>, ouvre le carnet d'un membre et clique sur « Demander l'accès complet ».
        </div>
      ) : (
        <div style={s.grid} data-testid="accompagnes-list">
          {members.map((m) => (
            <button key={m.id} style={s.card} data-testid={`accompagne-${m.member_id}`} onClick={() => nav(`/carnet/eleves/${m.member_id}`)}>
              {m.member_avatar ? <img src={m.member_avatar} alt="" style={s.avatar} /> : <span style={s.avatarFb}>{(m.member_name || "?").slice(0, 1).toUpperCase()}</span>}
              <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                <div style={s.name}>{m.member_name}</div>
                <div style={s.meta}><IoDocumentTextOutline size={13} /> {m.entry_count || 0} entrée{(m.entry_count || 0) > 1 ? "s" : ""} · accès complet</div>
              </div>
              <span style={s.open}><IoJournalOutline size={16} /> Ouvrir le carnet <IoChevronForward size={14} /></span>
            </button>
          ))}
        </div>
      )}
    </DojoShell>
  );
}

const s = {
  head: { display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 26 },
  badge: { width: 52, height: 52, borderRadius: 16, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "color-mix(in srgb, var(--brand) 14%, transparent)", color: RED },
  h1: { fontSize: 30, fontWeight: 900, color: "var(--text-primary)", fontFamily: "var(--font-display)", margin: 0 },
  sub: { color: "var(--text-secondary)", fontSize: 14, margin: "6px 0 0", maxWidth: 620, lineHeight: 1.6 },
  empty: { padding: "44px 22px", textAlign: "center", color: "var(--text-secondary)", border: "1px dashed var(--border-c)", borderRadius: 16, lineHeight: 1.7 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 14 },
  card: { display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", borderRadius: 18, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)", cursor: "pointer", width: "100%" },
  avatar: { width: 50, height: 50, borderRadius: "50%", objectFit: "cover", flexShrink: 0 },
  avatarFb: { width: 50, height: 50, borderRadius: "50%", background: RED, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 20, flexShrink: 0 },
  name: { fontWeight: 900, color: "var(--text-primary)", fontSize: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  meta: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, color: "var(--text-secondary)", marginTop: 3 },
  open: { display: "inline-flex", alignItems: "center", gap: 5, color: RED, fontWeight: 800, fontSize: 13, flexShrink: 0 },
};
